import React, { useMemo } from 'react';
import { transform } from 'sucrase';
import { RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * Live preview panel component
 * Transpiles JSX and renders it in an iframe
 */
export const PreviewPanel = ({ 
    fileContents, 
    previewKey, 
    onRefresh 
}) => {
    // Generate preview HTML with transpiled code
    const previewSrc = useMemo(() => {
        // Search for App.jsx/App.js in multiple possible locations
        const appPaths = [
            'App.jsx', 'App.js',
            'src/App.jsx', 'src/App.js',
            'frontend/src/App.jsx', 'frontend/src/App.js'
        ];
        
        let appCode = '';
        let foundPath = '';
        for (const path of appPaths) {
            if (fileContents[path]) {
                appCode = fileContents[path];
                foundPath = path;
                break;
            }
        }
        
        // Check if this is a complex app with alias imports (@/ imports)
        const hasAliasImports = appCode.includes("from '@/") || appCode.includes("from \"@/");
        
        // Check if app imports local context files (indicates complex app structure)
        const hasLocalContextImports = appCode.includes("from './context") || 
                                       appCode.includes("from \"./context") ||
                                       appCode.includes("from '../context") ||
                                       appCode.includes("from \"../context") ||
                                       appCode.includes("from './AuthContext") ||
                                       appCode.includes("from \"./AuthContext") ||
                                       appCode.includes("from './contexts") ||
                                       appCode.includes("from \"./contexts");
        
        if (!appCode.trim()) {
            return `
                <!DOCTYPE html>
                <html>
                <head>
                    <style>
                        body { 
                            font-family: system-ui, -apple-system, sans-serif;
                            display: flex; 
                            align-items: center; 
                            justify-content: center; 
                            height: 100vh; 
                            margin: 0;
                            background: #f9fafb;
                            color: #6b7280;
                        }
                    </style>
                </head>
                <body>
                    <div style="text-align: center;">
                        <p>No App.jsx or App.js file found</p>
                        <p style="font-size: 14px; margin-top: 8px;">Create an App.jsx or App.js file to see the preview</p>
                    </div>
                </body>
                </html>
            `;
        }
        
        // If the app uses alias imports or local context imports, show a helpful message
        if (hasAliasImports || hasLocalContextImports) {
            return `
                <!DOCTYPE html>
                <html>
                <head>
                    <style>
                        body { 
                            font-family: system-ui, -apple-system, sans-serif;
                            display: flex; 
                            align-items: center; 
                            justify-content: center; 
                            height: 100vh; 
                            margin: 0;
                            background: #fffbeb;
                            color: #92400e;
                        }
                        .container {
                            text-align: center;
                            padding: 20px;
                            max-width: 400px;
                        }
                        .icon {
                            font-size: 48px;
                            margin-bottom: 16px;
                            color: #b45309;
                        }
                        h3 {
                            margin: 0 0 8px;
                            color: #b45309;
                        }
                        p {
                            margin: 8px 0;
                            font-size: 14px;
                            line-height: 1.5;
                        }
                        .tip {
                            margin-top: 16px;
                            padding: 12px;
                            background: #fef3c7;
                            border-radius: 8px;
                            font-size: 13px;
                        }
                    </style>
                </head>
                <body>
                    <div class="container">
                        <div class="icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/>
                                <path d="m3.3 7 8.7 5 8.7-5"/>
                                <path d="M12 22V12"/>
                            </svg>
                        </div>
                        <h3>Complex Application Detected</h3>
                        <p>This application uses module imports (contexts, aliases) that cannot be resolved in the live preview.</p>
                        <div class="tip">
                            <strong>Tip:</strong> Deploy the application to see the full preview, or use "Open in Browser" (↗) for a basic preview.
                        </div>
                    </div>
                </body>
                </html>
            `;
        }

        try {
            let cleanCode = appCode;
            
            // Detect the exported component name BEFORE any transformations
            let componentName = 'App';
            
            // Pattern 1: export default ComponentName;
            const exportDefaultMatch = appCode.match(/export\s+default\s+(\w+)\s*;?\s*$/m);
            if (exportDefaultMatch) {
                componentName = exportDefaultMatch[1];
            }
            
            // Pattern 2: export default function ComponentName
            const exportFunctionMatch = appCode.match(/export\s+default\s+function\s+(\w+)/);
            if (exportFunctionMatch) {
                componentName = exportFunctionMatch[1];
            }
            
            // Pattern 3: export default class ComponentName
            const exportClassMatch = appCode.match(/export\s+default\s+class\s+(\w+)/);
            if (exportClassMatch) {
                componentName = exportClassMatch[1];
            }
            
            console.log('Detected component name:', componentName);
            
            // Remove ES6 imports
            cleanCode = cleanCode.replace(/import\s+[\s\S]*?from\s+['"][^'"]+['"];?\s*/g, '');
            cleanCode = cleanCode.replace(/import\s+['"][^'"]+['"];?\s*/g, '');
            
            // Handle export default function ComponentName() { ... }
            cleanCode = cleanCode.replace(/export\s+default\s+function\s+(\w+)\s*\(/g, 'function $1(');
            
            // Handle export default class ComponentName
            cleanCode = cleanCode.replace(/export\s+default\s+class\s+(\w+)/g, 'class $1');
            
            // Handle export default ComponentName; (just remove it)
            cleanCode = cleanCode.replace(/export\s+default\s+\w+\s*;?\s*$/gm, '');
            
            // Handle export default () => or export default (props) =>
            cleanCode = cleanCode.replace(/export\s+default\s+(\([^)]*\)\s*=>)/g, `var ${componentName} = $1`);
            cleanCode = cleanCode.replace(/export\s+default\s+(\(\s*\{[^}]*\}\s*\)\s*=>)/g, `var ${componentName} = $1`);
            
            // Handle named exports
            cleanCode = cleanCode.replace(/export\s+(const|let|var|function|class)\s+/g, '$1 ');
            cleanCode = cleanCode.replace(/export\s*\{[^}]*\}\s*(from\s*['"][^'"]+['"])?\s*;?/g, '');
            
            // Replace React.* with standalone functions
            cleanCode = cleanCode.replace(/React\.useState/g, 'useState');
            cleanCode = cleanCode.replace(/React\.useEffect/g, 'useEffect');
            cleanCode = cleanCode.replace(/React\.useCallback/g, 'useCallback');
            cleanCode = cleanCode.replace(/React\.useMemo/g, 'useMemo');
            cleanCode = cleanCode.replace(/React\.useRef/g, 'useRef');
            cleanCode = cleanCode.replace(/React\.useContext/g, 'useContext');
            cleanCode = cleanCode.replace(/React\.createContext/g, 'createContext');
            cleanCode = cleanCode.replace(/React\.useReducer/g, 'useReducer');
            cleanCode = cleanCode.replace(/React\.memo/g, 'memo');
            cleanCode = cleanCode.replace(/React\.forwardRef/g, 'forwardRef');
            cleanCode = cleanCode.replace(/React\.Fragment/g, 'Fragment');
            
            // Convert const/let to var for global scope
            cleanCode = cleanCode.replace(/^(const|let)\s+/gm, 'var ');

            // Transpile JSX
            const result = transform(cleanCode, {
                transforms: ['jsx'],
                jsxRuntime: 'classic',
                production: true
            });

            return `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="UTF-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                    <script src="https://unpkg.com/react@18/umd/react.development.js" crossorigin></script>
                    <script src="https://unpkg.com/react-dom@18/umd/react-dom.development.js" crossorigin></script>
                    <script src="https://cdn.tailwindcss.com"></script>
                    <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css">
                    <style>
                        * { box-sizing: border-box; }
                        body { margin: 0; font-family: system-ui, -apple-system, sans-serif; }
                    </style>
                </head>
                <body>
                    <div id="root"></div>
                    <script>
                        const { useState, useEffect, useCallback, useMemo, useRef, useContext, createContext, useReducer, memo, forwardRef, Fragment } = React;
                        
                        try {
                            ${result.code}
                            
                            // Try to find and render the component
                            var ComponentToRender = null;
                            
                            // Check for the detected component name
                            if (typeof ${componentName} !== 'undefined') {
                                ComponentToRender = ${componentName};
                            } else if (typeof App !== 'undefined') {
                                ComponentToRender = App;
                            }
                            
                            if (ComponentToRender) {
                                const root = ReactDOM.createRoot(document.getElementById('root'));
                                root.render(React.createElement(ComponentToRender));
                            } else {
                                document.getElementById('root').innerHTML = 
                                    '<div style="padding: 20px; color: #dc2626; font-family: monospace;">' +
                                    '<h3 style="margin: 0 0 10px;">Component Not Found</h3>' +
                                    '<pre style="white-space: pre-wrap; background: #fef2f2; padding: 12px; border-radius: 6px; font-size: 13px;">' + 
                                    'Could not find component: ${componentName}' + '</pre></div>';
                            }
                        } catch (error) {
                            document.getElementById('root').innerHTML = 
                                '<div style="padding: 20px; color: #dc2626; font-family: monospace;">' +
                                '<h3 style="margin: 0 0 10px;">Runtime Error</h3>' +
                                '<pre style="white-space: pre-wrap; background: #fef2f2; padding: 12px; border-radius: 6px; font-size: 13px;">' + 
                                error.message + '</pre></div>';
                            console.error('Preview error:', error);
                        }
                    </script>
                </body>
                </html>
            `;
        } catch (error) {
            return `
                <!DOCTYPE html>
                <html>
                <head>
                    <style>
                        body { 
                            font-family: ui-monospace, monospace;
                            padding: 20px;
                            background: #fef2f2;
                            color: #dc2626;
                        }
                        pre {
                            white-space: pre-wrap;
                            word-break: break-word;
                            background: white;
                            padding: 12px;
                            border-radius: 6px;
                            font-size: 13px;
                        }
                    </style>
                </head>
                <body>
                    <h3>Transpile Error</h3>
                    <pre>${error.message}</pre>
                </body>
                </html>
            `;
        }
    }, [fileContents, previewKey]);

    return (
        <div className="h-full flex flex-col bg-white">
            {/* Preview header */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50">
                <span className="text-sm font-medium text-gray-700">Live Preview</span>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onRefresh}
                    className="h-7"
                >
                    <RefreshCw className="w-3.5 h-3.5 mr-1" />
                    Refresh
                </Button>
            </div>
            
            {/* Preview iframe */}
            <div className="flex-1">
                <iframe
                    key={previewKey}
                    srcDoc={previewSrc}
                    title="Preview"
                    className="w-full h-full border-0"
                    sandbox="allow-scripts allow-same-origin"
                />
            </div>
        </div>
    );
};

export default PreviewPanel;
